import { useRef, useState } from 'react';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Field, Input } from '@/ui/Field';
import { useSubirFotosMasivas } from './api';

/**
 * FotosMasivas.tsx — subir un .zip con las fotos nombradas por cédula.
 *
 * ## Este diálogo tiene DOS puertas, y una viene de otro módulo
 *
 * `page-admin.js:115` llamaba a `PageDirectory.openBulkPhotos()` desde la barra
 * de gestión de usuarios. Es decir: **el Directorio exportaba una función que
 * Administración usaba**, y eso es lo que habría impedido borrar el archivo
 * vanilla al migrar.
 *
 * Las tres salidas posibles eran abrir otra puerta en el puente de React
 * —que `docs/Frontend/React.md` dice explícitamente que «no debería crecer»—,
 * duplicar el diálogo en Administración, o usar algo que ya existe.
 *
 * Se usó lo que ya existe: **los parámetros de ruta**. Administración hace
 * `APP.navigate('directory/fotos')` y esta pantalla abre el diálogo sola, igual
 * que `Perfil.tsx` abre su ficha con `#perfil/ficha`. Sin mecanismo nuevo, sin
 * copia, y una línea en `page-admin.js`.
 *
 * El efecto secundario es una mejora: quien sube fotos masivas acaba **en el
 * Directorio**, que es donde se ven las fotos que acaba de subir, en vez de en
 * la tabla de usuarios.
 */

export function FotosMasivas({ abierto, alCerrar }: { abierto: boolean; alCerrar: () => void }) {
  const subir = useSubirFotosMasivas();
  const [archivo, setArchivo] = useState<File | null>(null);
  const selector = useRef<HTMLInputElement>(null);

  function cerrar() {
    setArchivo(null);
    subir.reset();
    if (selector.current) selector.current.value = '';
    alCerrar();
  }

  const r = subir.data;
  const sinCoincidencia = r?.detail?.not_found ?? [];

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => { if (!v) cerrar(); }}
      titulo="Carga masiva de fotos"
      ancho="max-w-xl"
      pie={
        <>
          <Button variant="ghost" onClick={cerrar}>Cerrar</Button>
          <Button
            disabled={!archivo || subir.isPending}
            razonDeshabilitado={!archivo ? 'Elige primero un archivo .zip' : undefined}
            onClick={() => archivo && subir.mutate(archivo, {
              onSuccess: (res) =>
                Utils.toast(
                  `${res.updated} foto${res.updated === 1 ? ' actualizada' : 's actualizadas'}`,
                  'success',
                ),
            })}
          >
            <i className="bi bi-upload" aria-hidden="true" />
            {subir.isPending ? 'Subiendo…' : 'Subir'}
          </Button>
        </>
      }
    >
      <div className="rounded-sm bg-sunken p-3 text-3 text-ink-2">
        <strong>¿Cómo funciona?</strong> Sube un archivo <strong>.zip</strong> con las
        fotos. Cada imagen debe nombrarse con la <strong>cédula</strong> del
        colaborador, por ejemplo <code>1234567890.jpg</code>. Formatos: JPG o PNG.
      </div>

      <Field label="Archivo .zip" className="mt-4">
        <Input
          ref={selector}
          type="file"
          accept=".zip,application/zip"
          onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
        />
      </Field>

      {subir.isError && (
        <p role="alert" className="mt-4 text-3 text-danger-ink">
          {(subir.error as Error)?.message || 'No se pudo procesar el archivo.'}
        </p>
      )}

      {r && (
        <div className="mt-4">
          <p className="rounded-sm bg-ok-ink/12 px-3 py-2 text-3 text-ok-ink">
            <strong>{r.updated}</strong>{' '}
            {r.updated === 1 ? 'foto asignada' : 'fotos asignadas'}.
            {!!r.not_found && <><br />{r.not_found} sin coincidencia de cédula.</>}
            {!!r.errors && <><br />{r.errors} con error.</>}
          </p>
          {sinCoincidencia.length > 0 && (
            <div className="mt-2 text-2 text-muted">
              <p className="mb-1">Sin coincidencia:</p>
              {/* Una lista y no una cadena unida por comas: con veinte archivos
                  el vanilla dejaba una línea larguísima sin forma de leerla. */}
              <ul className="list-inside list-disc">
                {sinCoincidencia.map((x) => <li key={x.file}>{x.file}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
